import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../AuthContext';

const FooterPagination = ({
  currentPage,
  setCurrentPage,
  getFilteredMessages,
  isDarkMode,
  setIsDarkMode,
  getTotalPages,
  recordsPerPage,
  setRecordsPerPage,
  isMobile,
  edgeServerEndpoint,
  reverseSort
}) => {
  const { user } = useAuth();
  const totalMessages = getFilteredMessages().length;
  const totalPages = getTotalPages(totalMessages);

  // Guard against empty result sets so we don't show "1-0 of 0"
  const hasMessages = totalMessages > 0 && totalPages > 0;
  const safeCurrentPage = hasMessages ? Math.min(currentPage, totalPages) : 0;

  const startRecord = hasMessages ? (safeCurrentPage - 1) * recordsPerPage + 1 : 0;
  const endRecord = hasMessages ? Math.min(safeCurrentPage * recordsPerPage, totalMessages) : 0;

  // Save pagination preferences to backend
  const savePaginationPreferences = async (newRecordsPerPage, newCurrentPage) => {
    if (!user?.username) return;
    
    try {
      await axios.post(`${edgeServerEndpoint}/pagination-preferences/${user.username}`, {
        recordsPerPage: newRecordsPerPage,
        currentPage: newCurrentPage,
        reverseSort: reverseSort
      });
    } catch (error) {
      console.error('Failed to save pagination preferences:', error);
    }
  };

  // Handle records per page change
  const handleRecordsPerPageChange = (newValue) => {
    const newRecordsPerPage = Number(newValue);
    setRecordsPerPage(newRecordsPerPage);
    
    // Reset to first page when changing records per page
    const newCurrentPage = 1;
    setCurrentPage(newCurrentPage);
    
    // Save preferences
    savePaginationPreferences(newRecordsPerPage, newCurrentPage);
  };

  // Handle page navigation
  const handlePageChange = (newPage) => {
    if (!hasMessages) return;
    const clampedPage = Math.max(1, Math.min(newPage, totalPages));
    setCurrentPage(clampedPage);
    savePaginationPreferences(recordsPerPage, clampedPage);
  };

  // Classic pagination: compute visible page numbers window around current page
  const maxVisiblePages = 5;
  let startPage = 1;
  let endPage = totalPages;

  if (hasMessages) {
    if (totalPages > maxVisiblePages) {
      const halfWindow = Math.floor(maxVisiblePages / 2);
      startPage = Math.max(1, safeCurrentPage - halfWindow);
      endPage = startPage + maxVisiblePages - 1;
      if (endPage > totalPages) {
        endPage = totalPages;
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }
  } else {
    startPage = 0;
    endPage = 0;
  }

  const pageNumbers = [];
  for (let p = startPage; p <= endPage; p++) {
    if (p >= 1) {
      pageNumbers.push(p);
    }
  }

  const commonStyles = {
    background: isDarkMode ? 'bg-gray-900' : 'bg-white',
    borderColor: isDarkMode ? 'border-[#ff8b2d]/30' : 'border-gray-300',
    textColor: isDarkMode ? 'text-white' : 'text-gray-800',
    secondaryTextColor: isDarkMode ? 'text-[#ff8b2d]' : 'text-orange-600',
    buttonBackground: isDarkMode ? 'bg-[#ff8b2d]/10' : 'bg-gray-200',
    buttonHoverBackground: isDarkMode ? 'hover:bg-[#ff8b2d]/20' : 'hover:bg-gray-300',
    disabledOpacity: isDarkMode ? 'disabled:opacity-30' : 'disabled:opacity-50',
    borderBackground: isDarkMode ? 'bg-[#ff8b2d]/5' : 'bg-gray-100',
  };

  return (
    <div
      className={`sticky bottom-0 ${commonStyles.background} border-t ${commonStyles.borderColor} ${commonStyles.textColor} z-10`}
    >
      <div className="max-w-7xl mx-auto px-2 md:px-4">
        <div className={`flex items-center ${isMobile ? 'flex-col space-y-2 py-2' : 'justify-between h-16 space-x-4'}`}>
          <button
            onClick={() => {
              // Move one page toward the newest messages
              // reverseSort=true: newest at top (page 1)      -> decrement page
              // reverseSort=false: newest at bottom (last page)-> increment page
              const step = reverseSort ? -1 : 1;
              handlePageChange(safeCurrentPage + step);
            }}
            disabled={
              !hasMessages ||
              (reverseSort ? safeCurrentPage === 1 : safeCurrentPage === totalPages)
            }
            className={`flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-lg ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} ${commonStyles.borderColor} ${commonStyles.disabledOpacity} transition-all duration-300 group`}
          >
            <ChevronLeft
              size={isMobile ? 14 : 16}
              className={`mr-1 md:mr-2 ${commonStyles.secondaryTextColor} group-hover:transform group-hover:-translate-x-0.5 transition-transform`}
            />
            <span className={`${commonStyles.secondaryTextColor} font-medium text-sm md:text-base`}>NEW</span>
          </button>

          {/* Center section: range info + classic pagination controls */}
          <div className={`flex-1 flex flex-col items-center ${isMobile ? 'space-y-2' : 'space-y-1'}`}>
            <div className={`text-center px-3 py-1 rounded-lg ${commonStyles.borderBackground} ${commonStyles.borderColor} ${isMobile ? 'w-full' : 'w-auto'}`}>
              <span className="text-xs md:text-sm font-medium">
                {hasMessages ? (
                  <>
                    Showing{' '}
                    <span className={commonStyles.secondaryTextColor}>
                      {startRecord}-{endRecord}
                    </span>{' '}
                    of{' '}
                    <span className={commonStyles.secondaryTextColor}>
                      {totalMessages}
                    </span>
                  </>
                ) : (
                  'No messages to display'
                )}
              </span>
            </div>

            {/* Page indicator (numbers 1,2,3... are intentionally hidden) */}
            <div className="flex items-center space-x-1 md:space-x-2 text-xs md:text-sm">
              {hasMessages && (
                <span className={commonStyles.secondaryTextColor}>
                  Page {safeCurrentPage} of {totalPages}
                </span>
              )}
            </div>
          </div>

          {/* Records per page selector */}
          <div className={`flex items-center ${isMobile ? 'w-full justify-center mt-1' : 'space-x-2'}`}>
            {!isMobile && <label className="text-sm font-medium">Records:</label>}
            <select
              value={recordsPerPage}
              onChange={(e) => handleRecordsPerPageChange(e.target.value)}
              className={`px-2 py-1 rounded border ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} text-xs md:text-sm focus:ring focus:ring-orange-300 focus:outline-none`}
            >
              {[10, 20, 50, 100].map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {/* OLD button: move one page toward the oldest messages */}
          <button
            onClick={() => {
              // Move one page toward the oldest messages
              // reverseSort=true: oldest at bottom (last page) -> increment page
              // reverseSort=false: oldest at top (page 1)      -> decrement page
              const step = reverseSort ? 1 : -1;
              handlePageChange(safeCurrentPage + step);
            }}
            disabled={
              !hasMessages ||
              (reverseSort ? safeCurrentPage === totalPages : safeCurrentPage === 1)
            }
            className={`flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-lg ${commonStyles.buttonBackground} ${commonStyles.buttonHoverBackground} ${commonStyles.borderColor} ${commonStyles.disabledOpacity} transition-all duration-300 group`}
          >
            <span className={`${commonStyles.secondaryTextColor} font-medium text-sm md:text-base`}>OLD</span>
            <ChevronRight
              size={isMobile ? 14 : 16}
              className={`ml-1 md:ml-2 ${commonStyles.secondaryTextColor} group-hover:transform group-hover:translate-x-0.5 transition-transform`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FooterPagination;